// [NEW] MyTicketsScreen Component - Daftar tiket yang dimiliki user
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { HiOutlineCalendar, HiOutlineTicket } from 'react-icons/hi';

const MyTicketsScreen = ({ user, onBack, onTicketSelect }) => {
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' | 'past' | 'all'

    useEffect(() => {
        console.log("MyTicketsScreen Loaded - Optimized V4");

        if (!user?.uid) {
            setIsLoading(false);
            return;
        }

        const fetchTickets = async () => {
            setIsLoading(true);
            try {
                const ticketsRef = collection(db, "artifacts", appId, "public", "data", "users", user.uid, "tickets");
                const q = query(ticketsRef, orderBy("createdAt", "desc"));

                const snapshot = await getDocs(q);
                const ticketData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                console.log("Tickets fetched (One-time):", ticketData.length);
                setTickets(ticketData);
            } catch (err) {
                console.error("Error fetching tickets:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTickets();
    }, [user]);

    // Filter tickets by tab
    const filteredTickets = tickets.filter(ticket => {
        const now = new Date();
        const eventDate = ticket.eventDate?.toDate ? ticket.eventDate.toDate() : new Date(ticket.eventDate);

        if (activeTab === 'upcoming') {
            return eventDate > now && ticket.status !== 'refunded';
        } else if (activeTab === 'past') {
            return eventDate <= now || ticket.status === 'used';
        }
        return true; // 'all'
    });

    const getStatusBadge = (ticket) => {
        const now = new Date();
        const eventDate = ticket.eventDate?.toDate ? ticket.eventDate.toDate() : new Date(ticket.eventDate);

        if (ticket.status === 'refunded') {
            return { text: 'Refunded', color: 'bg-orange-100 text-orange-700' };
        }
        if (ticket.status === 'used' || ticket.checkedInAt) {
            return { text: 'Sudah Masuk', color: 'bg-gray-100 text-gray-600' };
        }
        if (eventDate < now) {
            return { text: 'Expired', color: 'bg-red-100 text-red-700' };
        }
        return { text: 'Valid', color: 'bg-green-100 text-green-700' };
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 p-4">
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">Silakan login untuk melihat tiket Anda</p>
                    <button onClick={onBack} className="text-green-700 underline">
                        Kembali
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 px-4 py-4 border-b">
                <div className="flex items-center mb-4">
                    <button onClick={onBack} className="mr-4 p-2 hover:bg-gray-100 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    </button>
                    <h1 className="text-2xl font-bold text-green-900">Tiket Saya</h1>
                </div>

                {/* Tabs */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('upcoming')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition ${activeTab === 'upcoming'
                            ? 'bg-green-700 text-white'
                            : 'bg-gray-100 text-gray-600'
                            }`}
                    >
                        Akan Datang
                    </button>
                    <button
                        onClick={() => setActiveTab('past')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition ${activeTab === 'past'
                            ? 'bg-green-700 text-white'
                            : 'bg-gray-100 text-gray-600'
                            }`}
                    >
                        Riwayat
                    </button>
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition ${activeTab === 'all'
                            ? 'bg-green-700 text-white'
                            : 'bg-gray-100 text-gray-600'
                            }`}
                    >
                        Semua
                    </button>
                </div>
            </div>

            {/* Tickets List */}
            <div className="p-4">
                {filteredTickets.length === 0 ? (
                    <div className="text-center py-12">
                        <HiOutlineTicket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500 mb-2">Belum ada tiket</p>
                        <p className="text-sm text-gray-400">Beli tiket event untuk melihatnya di sini</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredTickets.map(ticket => {
                            const status = getStatusBadge(ticket);

                            return (
                                <div
                                    key={ticket.id}
                                    onClick={() => onTicketSelect && onTicketSelect(ticket)}
                                    className="bg-white rounded-2xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition"
                                >
                                    <div className="p-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-bold text-green-900 flex-1 pr-2">
                                                {ticket.eventTitle}
                                            </h3>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color} whitespace-nowrap`}>
                                                {status.text}
                                            </span>
                                        </div>

                                        {ticket.categoryName && (
                                            <div className="inline-block px-2 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded mb-2">
                                                {ticket.categoryName}
                                            </div>
                                        )}

                                        <div className="flex items-center text-sm text-gray-600 mt-2">
                                            <HiOutlineCalendar className="w-4 h-4 mr-2 text-green-700" />
                                            <span>
                                                {ticket.eventDate?.toDate?.()?.toLocaleDateString('id-ID', {
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}
                                            </span>
                                        </div>

                                        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
                                            <span>ID: {ticket.id.substring(0, 12)}...</span>
                                            {ticket.pricePaid > 0 && (
                                                <span className="font-semibold text-green-700">
                                                    Rp {ticket.pricePaid.toLocaleString('id-ID')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Info Card */}
            {tickets.length > 0 && (
                <div className="mx-4 mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm text-blue-800">
                        💡 <strong>Tips:</strong> Ketuk tiket untuk melihat QR code dan detail lengkap
                    </p>
                </div>
            )}
        </div>
    );
};

export default MyTicketsScreen;
